/* R for Rabbit demo: page behaviours (tab bar, variant sync, gift option, countdown, popup). Runs after rfr-motion.js. Classic deferred script, no dependencies.
   Every part is progressive enhancement: the page works without it. */
(() => {
  const d = document;

  /* Tab bar: hides on scroll down and returns on scroll up through html[data-rfr-dir] (set by the one
     shared scroll loop in rfr-motion.js; CSS in rfr.css). */
  const bar = d.querySelector('.rfr-tabbar');
  if (bar) {
    /* Shopify's cookie banner is also fixed to the bottom: hide the tab bar while it shows (audit A3/B7,
       one fixed bottom element at a time). Watches body children only, then the banner's own attributes. */
    let bannerSeen = null;
    const syncBanner = () => {
      const banner = d.getElementById('shopify-pc__banner');
      d.documentElement.classList.toggle('rfr-pc-open', !!banner && banner.getClientRects().length > 0);
      if (banner && banner !== bannerSeen) {
        bannerSeen = banner;
        new MutationObserver(syncBanner).observe(banner, { attributes: true });
      }
    };
    new MutationObserver(() => requestAnimationFrame(syncBanner)).observe(d.body, { childList: true });
    syncBanner();

    /* "Shop by age" opens the menu drawer (age chips first); without JS it links to all products. */
    bar.querySelector('[data-rfr-menu]')?.addEventListener('click', (event) => {
      const summary = d.querySelector('#Details-menu-drawer-container > summary');
      if (!summary) return;
      event.preventDefault();
      summary.click();
    });
  }

  /* Announcement bar (Horizon's component): also pauses while a link in it has keyboard focus, and never
     rotates with reduced motion (the first message stays). */
  const ann = d.querySelector('announcement-bar-component');
  if (ann && window.customElements) {
    const still = matchMedia('(prefers-reduced-motion: reduce)');
    customElements.whenDefined('announcement-bar-component').then(() => {
      if (still.matches) ann.pause?.();
      ann.addEventListener('focusin', () => ann.suspend?.());
      ann.addEventListener('focusout', (event) => {
        if (!still.matches && !ann.contains(event.relatedTarget)) ann.resume?.();
      });
    });
  }

  /* Header menus with a panel ("Shop by age"): a click or Enter on the label opens the panel when it is
     closed (hover still opens it on mouse); once open, the label link goes to its page. Horizon's own
     disclosure button keeps working for keyboard users. */
  d.addEventListener('click', (event) => {
    const link = event.target instanceof Element && event.target.closest('#header-component .menu-list__link');
    if (!link) return;
    const header = link.closest('#header-component');
    const btn = link.closest('.menu-list__list-item')?.querySelector('.menu-list__disclosure');
    if (!btn || header?.dataset.menuStyle !== 'menu' || btn.getAttribute('aria-expanded') === 'true') return;
    event.preventDefault();
    btn.click();
  });

  /* Product page: blocks marked data-rfr-sync (stock cue, delivery line) are re-read from the HTML
     Horizon already fetched for the new variant. No extra request. */
  d.addEventListener(
    'shopify:product:select',
    (event) => {
      if (event.target instanceof Element && event.target.closest('product-card')) return;
      event.promise
        ?.then(({ detail }) => {
          const html = detail?.html;
          if (!html) return;
          d.querySelectorAll('[data-rfr-sync]').forEach((el) => {
            const fresh = html.querySelector('#' + el.id);
            if (!fresh) return;
            el.innerHTML = fresh.innerHTML;
            el.hidden = fresh.hidden;
          });
        })
        .catch(() => {});
    },
    true
  );

  /* Cart: "This is a gift" saved as a cart attribute right away (the message uses Horizon's cart note). */
  d.addEventListener('change', (event) => {
    const box = event.target;
    if (!(box instanceof HTMLInputElement) || !box.matches('[data-rfr-gift]')) return;
    fetch(window.Theme?.routes?.cart_update_url || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { Gift: box.checked ? 'Yes' : '' } }),
    }).catch(() => {});
  });

  /* Countdowns and campaign lines (data-rfr-end): minute resolution, digits are aria-hidden (the
     sentence next to them is static), hidden after the end date. The end is a fixed instant, so a
     reload never restarts it. */
  d.querySelectorAll('[data-rfr-end]').forEach((el) => {
    const end = Date.parse(el.dataset.rfrEnd);
    const units = el.querySelectorAll('[data-rfr-unit]');
    const tick = () => {
      const minutes = Math.ceil((end - Date.now()) / 60000);
      if (!(minutes > 0)) {
        el.hidden = true;
        return;
      }
      const parts = [Math.floor(minutes / 1440), Math.floor(minutes / 60) % 24, minutes % 60];
      units.forEach((unit, i) => (unit.textContent = String(parts[i]).padStart(2, '0')));
      if (units.length) setTimeout(tick, 60000 - (Date.now() % 60000) + 50);
    };
    tick();
  });

  /* Welcome popup (sections/rfr-welcome-popup.liquid). */
  const pop = d.getElementById('rfr-welcome');
  if (pop && pop.showModal) {
    const KEY = 'rfr-welcome-seen';
    const ds = pop.dataset;
    let seen = 0;
    let timer = 0;
    let returnFocus = null;
    try {
      seen = Number(localStorage.getItem(KEY)) || 0;
    } catch (e) {}
    const remember = () => {
      try {
        localStorage.setItem(KEY, String(ds.done != null ? 4e12 : Date.now()));
      } catch (e) {}
    };
    /* At >= 990px Horizon scrolls .page-wrapper, not the window: read RFR.scroller (rfr-motion.js). */
    let armed = false;
    const onScroll = () => {
      if (!armed) return;
      const sc = window.RFR && window.RFR.scroller ? window.RFR.scroller : d.scrollingElement;
      const max = sc.scrollHeight - sc.clientHeight;
      if (max > 0 && sc.scrollTop / max >= (Number(ds.scroll) || 50) / 100) show();
    };
    const disarm = () => {
      clearTimeout(timer);
      armed = false;
    };
    function show() {
      if (pop.open) return;
      if (d.querySelector('dialog[open], #Details-menu-drawer-container[open]')) {
        disarm();
        timer = setTimeout(show, 5000);
        return;
      }
      disarm();
      returnFocus = d.activeElement;
      pop.showModal();
      remember();
    }
    pop.addEventListener('click', (event) => {
      if (event.target === pop || event.target.closest('[data-rfr-close]')) pop.close();
      const copy = event.target.closest('[data-rfr-copy]');
      if (copy && navigator.clipboard) {
        navigator.clipboard.writeText(copy.dataset.rfrCopy).then(() => (copy.textContent = 'Copied'));
      }
    });
    pop.addEventListener('close', () => returnFocus?.focus?.());
    if (ds.open != null) {
      show();
    } else if (Date.now() - seen > (Number(ds.days) || 7) * 864e5) {
      timer = setTimeout(show, (Number(ds.delay) || 15) * 1000);
      if (window.RFR && window.RFR.onScroll) window.RFR.onScroll(onScroll);
      armed = true;
    }
  }
})();
